import React, { useState, useEffect, useMemo } from 'react';
import {
  Sparkles, CheckCircle2, XCircle, AlertTriangle, HelpCircle,
  User, Calendar, Clock, ArrowRight, Shield, Star, Award,
  MapPin, RefreshCw, Eye, ThumbsUp, ThumbsDown, Check, Trash2,
  FileText, ShieldAlert, ChevronDown, ChevronUp, History,
  Mail, ExternalLink, Send
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import DocumentTemplateMultiSelect from '../shared/DocumentTemplateMultiSelect';

interface WorkspaceAIStatus {
  configured: boolean;
  provider?: string;
  modelId?: string;
  message?: string;
}

interface Suggestion {
  id: string;
  type: 'document_review' | 'program_eligibility' | 'job_matching' | 'partner_suggestions' | 'red_flag';
  targetRecordId: string;
  targetRecordCollection: string;
  confidence: number | null;
  reasoning: string;
  status: 'pending' | 'pending_review' | 'accepted' | 'overridden' | 'ignored';
  reviewedBy: string | null;
  reviewedAt: string | null;
  suggestedData: any;
  applicantId: string;
  applicationId: string;
  applicantName?: string;
  applicantEmail?: string;
  createdAt: string;
  modelTier?: 'cheap' | 'escalated';
  modelUsed?: string;
  payload?: any;
}

interface GroupedApplicantSuggestions {
  applicationId: string;
  applicantId: string;
  applicantName: string;
  applicantEmail: string;
  suggestions: Suggestion[];
  highestConfidence: number;
}

interface AiSuggestionsManagerProps {
  canEdit?: boolean;
  onViewApplicant?: (app: any) => void;
}

export default function AiSuggestionsManager({ canEdit = true, onViewApplicant }: AiSuggestionsManagerProps = {}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveTab] = useState<string>('all');
  const [activeStatus, setActiveStatus] = useState<string>('pending_review'); // Default view shows ONLY pending items
  const [expandedAppIds, setExpandedAppIds] = useState<Set<string>>(new Set());
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<WorkspaceAIStatus | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  // Email draft summary modal state
  const [draftModalAppId, setDraftModalAppId] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftData, setDraftData] = useState<{ to: string; subject: string; body: string; applicationId: string } | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Document templates to generate from the drafted applicant's
  // application and attach when the summary email is sent (Slice 3).
  const [draftDocumentTemplateIds, setDraftDocumentTemplateIds] = useState<string[]>([]);

  const loadWorkspaceAIStatus = async () => {
    try {
      const data = await safeFetch('/api/workspaces/settings/llm');
      const cfg = data?.llmConfig || {};
      const configured = Boolean(cfg.provider && cfg.modelId && cfg.apiKey);
      setAiStatus({
        configured,
        provider: cfg.provider,
        modelId: cfg.modelId,
        message: configured
          ? `Using workspace LLM: ${cfg.provider} / ${cfg.modelId}`
          : 'Workspace AI is not configured. Ask a workspace administrator to configure and test an LLM model in Workspace Settings.'
      });
      return configured;
    } catch (err: any) {
      const message = err.message || 'Unable to load workspace AI configuration.';
      setAiStatus({ configured: false, message });
      return false;
    }
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    setPageError(null);
    try {
      await loadWorkspaceAIStatus();
      let url = `/api/admin/ai/suggestions`;
      const params: string[] = [];
      if (activeStatus !== 'all') params.push(`status=${activeStatus}`);
      if (activeType !== 'all') params.push(`type=${activeType}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await safeFetch(url);
      setSuggestions(Array.isArray(res.suggestions) ? res.suggestions : []);
    } catch (err: any) {
      const message = err.message || 'Failed to fetch AI suggestions.';
      setPageError(message);
      notifyToast('error', message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [activeType, activeStatus]);

  const handleAction = async (id: string, action: 'accept' | 'override' | 'ignore', overrideData?: any) => {
    setProcessingId(id);
    try {
      const res = await safeFetch(`/api/admin/ai/suggestions/${id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, overrideData })
      });
      if (res.success) {
        notifyToast('success', `Suggestion successfully ${action === 'accept' ? 'accepted' : action === 'override' ? 'overridden' : 'ignored'}.`);
        fetchSuggestions();
      }
    } catch (err: any) {
      if (err?.status === 409) {
        // The server refused because this suggestion was already
        // processed (e.g. a double-click race or a stale list). That is
        // the recorded decision standing — not a failure. Refresh so
        // the row shows its processed state.
        notifyToast('info', 'That suggestion was already processed — the recorded decision stands.');
        fetchSuggestions();
      } else {
        notifyToast('error', err.message || 'Action failed.');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const toggleAppExpanded = (appId: string) => {
    setExpandedAppIds(prev => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  };

  // Open applicant detail popup
  const handleViewApplicant = async (appId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!appId || appId === 'unassigned') return;
    try {
      const res = await safeFetch(`/api/admin/applications/${appId}/full`);
      const appData = res.application || res;
      if (onViewApplicant) {
        onViewApplicant(appData);
      } else {
        notifyToast('info', `Applicant Record #${appId} loaded.`);
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Unable to open candidate details.');
    }
  };

  // Generate Email Draft Summary
  const handleDraftEmail = async (appId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftModalAppId(appId);
    setDraftDocumentTemplateIds([]);
    setDraftLoading(true);
    try {
      const res = await safeFetch('/api/admin/ai/draft-summary-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId: appId }),
      });
      if (res.success && res.draft) {
        setDraftData({
          to: res.draft.to || '',
          subject: res.draft.subject || '',
          body: res.draft.textBody || res.draft.htmlBody || '',
          applicationId: appId,
        });
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to draft summary email.');
      setDraftModalAppId(null);
    } finally {
      setDraftLoading(false);
    }
  };

  const handleSendDraftEmail = async () => {
    if (!draftData || !draftData.to || !draftData.subject || !draftData.body) {
      notifyToast('error', 'Recipient email, subject, and body are required.');
      return;
    }
    setSendingEmail(true);
    try {
      await safeFetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: draftData.to,
          subject: draftData.subject,
          htmlBody: draftData.body.replace(/\n/g, '<br/>'),
          applicationId: draftData.applicationId,
          // Generated server-side from this applicant's application and
          // attached to the email (Slice 3).
          documentTemplateIds: draftDocumentTemplateIds.length > 0 ? draftDocumentTemplateIds : undefined,
        }),
      });
      notifyToast('success', 'Summary email successfully dispatched to applicant!');
      setDraftModalAppId(null);
      setDraftData(null);
      setDraftDocumentTemplateIds([]);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to dispatch email.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Group suggestions by applicationId (or applicant)
  const groupedSuggestions = useMemo(() => {
    const map = new Map<string, GroupedApplicantSuggestions>();

    suggestions.forEach((sug) => {
      // Hide actioned suggestions when viewing Pending Review
      if (
        (activeStatus === 'pending_review' || activeStatus === 'pending') &&
        (sug.status === 'accepted' || sug.status === 'overridden' || sug.status === 'ignored')
      ) {
        return;
      }

      const key = sug.applicationId || sug.applicantId || 'unassigned';
      const name = sug.applicantName || sug.suggestedData?.applicantName || sug.applicantId || 'Applicant';
      const email = sug.applicantEmail || sug.suggestedData?.applicantEmail || '';

      if (!map.has(key)) {
        map.set(key, {
          applicationId: sug.applicationId || key,
          applicantId: sug.applicantId || '',
          applicantName: name,
          applicantEmail: email,
          suggestions: [],
          highestConfidence: sug.confidence || 0,
        });
      }

      const existing = map.get(key)!;
      existing.suggestions.push(sug);
      if ((sug.confidence || 0) > existing.highestConfidence) {
        existing.highestConfidence = sug.confidence;
      }
    });

    return Array.from(map.values());
  }, [suggestions, activeStatus]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'document_review': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'program_eligibility': return <Award className="h-4 w-4 text-emerald-500" />;
      case 'job_matching': return <Star className="h-4 w-4 text-amber-500" />;
      case 'partner_suggestions': return <Sparkles className="h-4 w-4 text-indigo-500" />;
      case 'red_flag': return <ShieldAlert className="h-4 w-4 text-rose-500" />;
      default: return <HelpCircle className="h-4 w-4 text-slate-500" />;
    }
  };

  const getLabel = (type: string) => {
    switch (type) {
      case 'document_review': return 'Document Compliance';
      case 'program_eligibility': return 'Program Eligibility';
      case 'job_matching': return 'Job Recommendations';
      case 'partner_suggestions': return 'Sponsor Suggestions';
      case 'red_flag': return 'Risk Alerts (Red Flags)';
      default: return type;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto animate-fade-in">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-indigo-600 animate-pulse" />
            AI Suggestions Review Queue
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-medium">
            Verify and approve AI-generated recommendations grouped by applicant before committing to target databases.
          </p>
        </div>
        <button onClick={fetchSuggestions} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-800 transition-all cursor-pointer" title="Refresh queue">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {aiStatus && (
        <div className={`rounded-2xl border p-4 flex items-start gap-3 ${aiStatus.configured ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          {aiStatus.configured ? <Shield className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
          <div>
            <p className="text-xs font-black uppercase tracking-wider">Workspace AI Engine</p>
            <p className="text-xs font-semibold mt-1">{aiStatus.message}</p>
            <p className="text-[11px] mt-1 opacity-80">All AI-powered portal features are routed through the workspace-configured model.</p>
          </div>
        </div>
      )}

      {pageError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3 text-rose-800">
          <XCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-xs font-black uppercase tracking-wider">Unable to load AI Suggestions</p>
            <p className="text-xs font-semibold mt-1">{pageError}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-1">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {['all', 'document_review', 'program_eligibility', 'job_matching', 'partner_suggestions', 'red_flag'].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                activeType === t
                  ? 'bg-brand-primary text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t === 'all' ? 'All Types' : getLabel(t)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 shrink-0 self-end">
          <button
            onClick={() => setActiveStatus('pending_review')}
            className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
              activeStatus === 'pending_review' || activeStatus === 'pending'
                ? 'bg-brand text-white shadow-sm'
                : 'bg-slate-100 border border-transparent text-slate-600 hover:bg-slate-200'
            }`}
          >
            Pending Review
          </button>
          <button
            onClick={() => setActiveStatus('accepted')}
            className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1 ${
              activeStatus === 'accepted' || activeStatus === 'ignored' || activeStatus === 'all'
                ? 'bg-brand-primary text-white shadow-sm'
                : 'bg-slate-100 border border-transparent text-slate-600 hover:bg-slate-200'
            }`}
          >
            <History className="h-3.5 w-3.5" /> History / Actioned
          </button>
        </div>
      </div>

      {/* Grouped Suggestions List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="premium-loader" />
        </div>
      ) : groupedSuggestions.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl p-8">
          <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-sm font-bold text-slate-700">Review queue is clean</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            All AI suggestions in this category have been actioned. New entries will appear when fresh runs complete.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {groupedSuggestions.map((group) => {
            const isExpanded = expandedAppIds.has(group.applicationId);
            const latestInvocationDate = group.suggestions.length > 0 && group.suggestions[0].createdAt
              ? new Date(group.suggestions[0].createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
              : 'Recently Invoked';

            return (
              <div key={group.applicationId} className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs hover:shadow-md transition-all">
                {/* Applicant Group Header Card */}
                <div
                  onClick={() => toggleAppExpanded(group.applicationId)}
                  className="p-5 bg-slate-50/70 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-slate-100/60 transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-11 w-11 rounded-2xl bg-indigo-100 text-indigo-700 font-extrabold flex items-center justify-center shrink-0 text-sm shadow-2xs">
                      {group.applicantName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Hyperlink Applicant Name to launch Candidate Detail Popup */}
                        <button
                          onClick={(e) => handleViewApplicant(group.applicationId, e)}
                          className="text-sm font-black text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1.5 cursor-pointer text-left group/link"
                          title="Click to open full applicant detail popup"
                        >
                          <span>{group.applicantName}</span>
                          <ExternalLink className="h-3.5 w-3.5 text-indigo-400 group-hover/link:text-indigo-600 transition-colors" />
                        </button>
                        <span className="text-caption font-mono text-slate-400">#{group.applicationId}</span>
                      </div>

                      {/* Hyperlink Applicant Email */}
                      {group.applicantEmail && (
                        <p
                          onClick={(e) => handleViewApplicant(group.applicationId, e)}
                          className="text-xs text-slate-500 hover:text-indigo-600 hover:underline mt-0.5 truncate font-medium cursor-pointer"
                          title="Click to open full applicant detail popup"
                        >
                          {group.applicantEmail}
                        </p>
                      )}

                      {/* Invocation Date Badge & Pending Types */}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-bold bg-slate-200/70 text-slate-700">
                          <Clock className="h-3 w-3 text-slate-500" />
                          Invoked: {latestInvocationDate}
                        </span>

                        {group.suggestions.map((s) => (
                          <span
                            key={s.id}
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-bold bg-white border border-slate-200 text-slate-700 shadow-2xs"
                          >
                            {getIcon(s.type)}
                            {getLabel(s.type)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0 justify-between sm:justify-end">
                    {/* Draft Summary Email Button */}
                    <button
                      onClick={(e) => handleDraftEmail(group.applicationId, e)}
                      className="px-3 py-1.5 border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl text-xs font-extrabold cursor-pointer transition-all flex items-center gap-1.5 shadow-2xs"
                      title="Draft an actionable summary email based on eligibility & document review"
                    >
                      <Mail className="h-3.5 w-3.5 text-indigo-600" />
                      <span>Draft Summary Email</span>
                    </button>

                    <div className="text-right pl-2 border-l border-slate-200">
                      <span className="text-[10px] text-slate-400 block font-bold uppercase tracking-wider">Top Confidence</span>
                      <span className="text-base font-black text-emerald-600">{group.highestConfidence}%</span>
                    </div>

                    <div className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500">
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Individual Suggestions Body */}
                {isExpanded && (
                  <div className="p-5 divide-y divide-slate-100 space-y-6">
                    {group.suggestions.map((sug) => (
                      <div key={sug.id} className="pt-4 first:pt-0">
                        {/* Suggestion Item Header */}
                        <div className="flex items-center justify-between gap-4 mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {getIcon(sug.type)}
                            <span className="text-xs font-black text-slate-900 uppercase tracking-wider">{getLabel(sug.type)}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase border ${
                              sug.status === 'pending' || sug.status === 'pending_review'
                                ? 'bg-amber-50 text-amber-800 border-amber-200'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }`}>{sug.status}</span>

                            <span className="text-[11px] text-slate-500 font-medium">
                              Invoked on {sug.createdAt ? new Date(sug.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                            </span>
                          </div>

                          <div className="text-right flex items-center gap-2">
                            {sug.modelTier && (
                              <span className="px-2 py-0.5 text-[11px] font-extrabold uppercase rounded bg-purple-50 text-purple-700 border border-purple-200">
                                {sug.modelTier} tier
                              </span>
                            )}
                            <span className="text-sm font-black text-emerald-600">{typeof sug.confidence === 'number' ? `${sug.confidence}%` : '—'}</span>
                          </div>
                        </div>

                        {/* Reasoning */}
                        <div className="bg-slate-50 rounded-2xl p-3.5 border border-slate-100 mb-3 text-xs text-slate-700 font-medium">
                          {sug.reasoning}
                        </div>

                        {/* Specific Content Details */}
                        {sug.type === 'document_review' && (
                          <div className="p-3 bg-white border border-slate-100 rounded-2xl space-y-2 mb-3 text-xs">
                            <p className="font-bold text-slate-600">Covered Document: <span className="text-slate-900">{sug.suggestedData?.docName || sug.targetRecordId}</span></p>
                            {((sug as any).aiReview?.model || sug.modelUsed) && (
                              <p className="font-bold text-slate-600">AI model: <span className="text-slate-900">{(sug as any).aiReview?.model || sug.modelUsed}</span></p>
                            )}
                            {(sug as any).payload?.identity?.status && (
                              <p className="font-bold text-slate-600">Identity cross-check:{' '}
                                <span className={(sug as any).payload.identity.status === 'mismatch' ? 'text-red-600 font-black' : 'text-slate-900'}>
                                  {(sug as any).payload.identity.status}
                                </span>
                              </p>
                            )}
                            {(sug as any).payload?.vision && (
                              <p className="font-bold text-slate-600">Vision inspection:{' '}
                                <span className="text-slate-900">
                                  {(sug as any).payload.vision.ran
                                    ? (sug as any).payload.vision.status
                                    : `not run${(sug as any).payload.vision.reason ? ` — ${(sug as any).payload.vision.reason}` : ''}`}
                                </span>
                              </p>
                            )}
                            {(sug as any).payload?.engine && (
                              <p className="font-bold text-slate-600">Scanned by: <span className="text-slate-900">{(sug as any).payload.engine === 'document-ai' || (sug as any).payload.engine === 'document-ai-id' ? 'Document AI' : `built-in engine (${(sug as any).payload.engine})`}</span></p>
                            )}
                            {sug.suggestedData?.checks && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                {sug.suggestedData.checks.map((chk: any, ci: number) => (
                                  <div key={ci} className="flex items-center gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-100 text-[11px]">
                                    {chk.passed ? <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />}
                                    <span className="font-semibold text-slate-700">{chk.name}: {chk.details || (chk.passed ? 'Verified' : 'Flagged')}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {sug.type === 'program_eligibility' && sug.suggestedData?.criteriaBreakdown && (
                          <div className="space-y-1.5 mb-3">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Criteria Breakdown</p>
                            <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden bg-white text-xs">
                              {sug.suggestedData.criteriaBreakdown.map((crit: any, idx: number) => (
                                <div key={idx} className="p-2.5 flex items-center justify-between gap-3">
                                  <span className="font-bold text-slate-800">{crit.criterion}</span>
                                  <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${
                                    crit.status === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'
                                  }`}>{crit.status}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {(sug.status === 'pending' || sug.status === 'pending_review') && canEdit && (
                          <div className="flex items-center gap-2 justify-end pt-2">
                            <button
                              onClick={() => handleAction(sug.id, 'ignore')}
                              disabled={processingId !== null}
                              className="px-3.5 py-1.5 border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-xl text-xs font-bold cursor-pointer transition-all disabled:opacity-50"
                            >
                              Ignore
                            </button>
                            <button
                              onClick={() => handleAction(sug.id, 'accept')}
                              disabled={processingId !== null}
                              className="px-4 py-1.5 btn-primary text-white rounded-xl text-xs font-extrabold cursor-pointer transition-all disabled:opacity-50 flex items-center gap-1"
                            >
                              <Check className="h-3.5 w-3.5" /> Accept
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Email Review & Dispatch Modal ─────────────────────────────── */}
      {draftModalAppId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 space-y-5 shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Review & Edit Summary Email</h3>
                  <p className="text-xs text-slate-500">
                    Review the AI-drafted notification email summarizing actionable steps for applicant #{draftModalAppId}.
                  </p>
                </div>
              </div>

              <button aria-label="Close" title="Close"
                onClick={() => { setDraftModalAppId(null); setDraftData(null); }}
                className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {draftLoading ? (
              <div className="py-12 flex flex-col items-center justify-center space-y-3">
                <div className="premium-loader" />
                <p className="text-xs font-bold text-slate-600">Analyzing eligibility and document assessments...</p>
              </div>
            ) : draftData ? (
              <div className="space-y-4 text-xs">
                <div>
                  <label className="block font-extrabold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Recipient Email</label>
                  <input
                    type="email"
                    value={draftData.to}
                    onChange={(e) => setDraftData({ ...draftData, to: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-slate-200 font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block font-extrabold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Subject Line</label>
                  <input
                    type="text"
                    value={draftData.subject}
                    onChange={(e) => setDraftData({ ...draftData, subject: e.target.value })}
                    className="w-full p-2.5 rounded-xl border border-slate-200 font-bold focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block font-extrabold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">Email Body (Editable)</label>
                  <textarea
                    rows={10}
                    value={draftData.body}
                    onChange={(e) => setDraftData({ ...draftData, body: e.target.value })}
                    className="w-full p-3 rounded-2xl border border-slate-200 font-medium leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-primary bg-slate-50/50"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    The admin can freely modify or add specific instructions before sending to the candidate.
                  </p>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <DocumentTemplateMultiSelect
                    value={draftDocumentTemplateIds}
                    onChange={setDraftDocumentTemplateIds}
                    label="Generate & Attach Documents"
                    hint="Generated from this applicant's application when the email is sent, saved to their Generated Documents, and attached as PDFs."
                    disabled={sendingEmail}
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => { setDraftModalAppId(null); setDraftData(null); }}
                    disabled={sendingEmail}
                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendDraftEmail}
                    disabled={sendingEmail}
                    className="px-5 py-2 btn-primary text-white font-extrabold rounded-xl cursor-pointer flex items-center gap-2 shadow-sm disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    <span>{sendingEmail ? 'Sending...' : 'Send Email to Applicant'}</span>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
